import { ReactElement } from 'react';
import { scLight } from './shared/Theme';
import { ThemeProvider } from 'styled-components';
import Playground from './pages/Playground';
import SharedConversation from './pages/SharedConversation';
import { ChatControlProvider } from './shared/context/ChatControlContext';
import { CustomModelsProvider } from './shared/context/CustomModelsContext';
import { ERPConnectionsProvider } from './shared/context/ERPConnectionsContext';
import { FeatureFlagsProvider } from './shared/context/FeatureFlagsContext';
import Login from './pages/Login';
import { useEffect } from 'react';
import { captureGoogleAdsParamsFromUrl, attributeGoogleAdsClickToAuthedUser } from './utils/googleAdsTracking';
import CheckoutRedirect from './pages/CheckoutRedirect';
import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import 'react-toastify/dist/ReactToastify.css';
import * as Sentry from "@sentry/react";
import { replayIntegrationOptions, sentryInitOptions, shouldRecordReplayForEvent } from './utils/sentryConfig';

const isProduction = process.env.REACT_APP_NODE_ENV === 'production';

if (isProduction) {
  Sentry.init({
    dsn: "https://ca799da2c32751851680b0d7fd1ec105@o4510668927860736.ingest.us.sentry.io/4510674871255040",
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        ...replayIntegrationOptions,
        // Handled (non-crash) errors are tagged handled:true and must NOT pull a
        // replay — see shouldRecordReplayForEvent. Protects the replay quota.
        beforeErrorSampling: shouldRecordReplayForEvent,
      }),
    ],
    ...sentryInitOptions,
  });
}


type ProtectedRouteProps = {
  children: ReactElement;
};

function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuth0();
  const location = useLocation();

  if (isLoading) {
    return null;
  }

  if (!isAuthenticated) {
    const params = new URLSearchParams(location.search);
    const hasAuthErrorParams = params.has('error') || params.has('error_description');
    const search = hasAuthErrorParams ? `?${params.toString()}` : '';
    return <Navigate to={`/auth${search}`} replace />;
  }

  return children;
}




function GoogleAdsTracker() {
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  useEffect(() => { captureGoogleAdsParamsFromUrl(); }, []);
  useEffect(() => {
    if (isLoading || !isAuthenticated) return;
    attributeGoogleAdsClickToAuthedUser(() => getAccessTokenSilently());
  }, [isLoading, isAuthenticated, getAccessTokenSilently]);
  return null;
}


function App() {
  return (
    <ThemeProvider theme={scLight}>
      <FeatureFlagsProvider>
      <Auth0Provider
      domain={process.env.REACT_APP_AUTH_DOMAIN as string}
      clientId={process.env.REACT_APP_AUTH_CLIENT_ID as string}
      cacheLocation='localstorage'
      authorizationParams={{
        redirect_uri: window.location.origin + "/auth",
        audience: process.env.REACT_APP_AUTH0_AUDIENCE_URL,
        scope: "chat:messages profile email offline_access",
      }}
      useRefreshTokens={true}
      useRefreshTokensFallback={true}
      >
        <ChatControlProvider>
          <CustomModelsProvider>
            <ERPConnectionsProvider>
            <Router>
              <GoogleAdsTracker />
              <Routes>
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <Playground/>
                    </ProtectedRoute>
                  }
                />
                <Route path="/auth" element={<Login />}/>
                <Route path="/shared/:shareId" element={<SharedConversation />}/>
                <Route path="/checkout/local/sabia2-small" element={<CheckoutRedirect model_variant='small'/>}/>
                <Route path="/checkout/local/sabia2-medium" element={<CheckoutRedirect model_variant='medium'/>}/>
              </Routes>
            </Router>
            </ERPConnectionsProvider>
          </CustomModelsProvider>
        </ChatControlProvider>
      </Auth0Provider>
      </FeatureFlagsProvider>
    </ThemeProvider>
  );
}

export default App;
